"use client";

import {useState} from "react";
import {ChevronDown, ChevronUp} from "lucide-react";

interface FAQItem {
    question: string;
    answer: string | React.ReactNode;
}

const faqData: FAQItem[] = [
    {
        question: "What is WiderTalk?",
        answer: "WiderTalk is an innovative live commentary platform that allows you to provide real-time commentary on events, sports, and shows. Whether you're an expert commentator or just passionate about a topic, WiderTalk gives you the tools to share your insights with a global audience."
    },
    {
        question: "Can anyone commentate? How do I start commentating?",
        answer: (
            <>
                Yes! Getting started is easy. Simply sign up for an account, complete your profile, and you can begin
                commentating on available events. Check out our{' '}
                <a href="/quick-start" className="text-widertalk-navy hover:underline font-medium">
                    Quick Start Guide
                </a>
                {' '}for detailed instructions on setting up your first commentary session.
            </>
        )
    },
    {
        question: "What equipment do I need?",
        answer: "All you need is a device with internet access and a microphone. For the best experience, we recommend using a good quality headset or external microphone. Our platform works on desktop browsers, tablets, and mobile devices."
    },
    {
        question: "Can I commentate with others?",
        answer: "Yes! WiderTalk supports multi-host commentary sessions. You can invite other commentators to join your session and provide collaborative commentary on events."
    },
    {
        question: "Is WiderTalk free to use?",
        answer: "WiderTalk is offering free access for the 2025 Tour of Guangxi for the first 1000 sign-ups. After that it is $3 (USD) to watch or commentate for the duration of the 6 day event. Our exact pricing for future events is still to be established, but we are committed to providing a quality service for our community."
    },
    {
        question: "How do I handle technical issues during a live session?",
        answer: (
            <>
                If you experience technical issues during a live commentary session, first try refreshing your browser.
                If
                problems persist, check your internet connection and audio settings. If that still doesn't resolve the
                issue, either{' '}
                <a href="/feedback" className="text-widertalk-navy hover:underline font-medium">
                    raise a bug
                </a>
                {' '}or{' '}
                <a href="/contact" className="text-widertalk-navy hover:underline font-medium">
                    contact our support team directly.
                </a>
            </>
        )
    },
    {
        question: "How do I report inappropriate content or behavior?",
        answer: "We take community safety seriously. You can report inappropriate content or behavior using the report button available during sessions, or by contacting our team directly. All reports are reviewed promptly and appropriate action is taken."
    },
    {
        question: "Can Commentators earn money from WiderTalk?",
        answer: (
            <>
                We are still working through the exact revenue share model, but we intend to reward the most successful commentators as you would see on other platforms such as Youtube or Twitch. We are not currently offering this for the Tour of Guangxi 2025 event, but it is something we are actively working on for future events.
                <br />
                <br />
                Commentators are allowed to use personal sponsorships and promotions during their commentary sessions (such as in podcasting), provided they comply with our terms and conditions. Check out our{' '}
                <a href="/terms-and-conditions" className="text-widertalk-navy hover:underline font-medium">
                    Terms and Conditions
                </a>
                {' '}for detailed instructions. Most importantly prior approval is required as per Section 5.2.
            </>
        )
    },
    {
        question: "Do you own the sports rights?",
        answer: "Yes, we have secured the necessary rights to stream our events. We are committed to complying with all relevant broadcasting and content regulations."
    }
];

export default function FAQPage() {
    const [openItems, setOpenItems] = useState<number[]>([]);

    const toggleItem = (index: number) => {
        setOpenItems(prev =>
            prev.includes(index)
                ? prev.filter(item => item !== index)
                : [...prev, index]
        );
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl mx-auto px-4 py-12">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold text-widertalk-navy mb-4"
                        style={{fontFamily: 'Gendy, sans-serif'}}>
                        Frequently Asked Questions
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                        Find answers to common questions about WiderTalk and how to get the most out of our platform.
                    </p>
                </div>

                {/* FAQ Items */}
                <div className="space-y-4">
                    {faqData.map((item, index) => (
                        <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200">
                            <button
                                onClick={() => toggleItem(index)}
                                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                            >
                                <h3 className="text-lg font-semibold text-widertalk-navy pr-4">
                                    {item.question}
                                </h3>
                                {openItems.includes(index) ? (
                                    <ChevronUp className="w-5 h-5 text-widertalk-navy flex-shrink-0"/>
                                ) : (
                                    <ChevronDown className="w-5 h-5 text-widertalk-navy flex-shrink-0"/>
                                )}
                            </button>
                            {openItems.includes(index) && (
                                <div className="px-6 pb-4">
                                    <p className="text-gray-700 leading-relaxed">
                                        {item.answer}
                                    </p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Contact Section */}
                <div className="mt-12 text-center">
                    <div className="bg-widertalk-navy rounded-lg p-8 text-white">
                        <h2 className="text-2xl font-bold mb-4" style={{fontFamily: 'Gendy, sans-serif'}}>
                            Still have questions?
                        </h2>
                        <p className="text-widertalk-cream mb-6">
                            Can't find the answer you're looking for? Our support team is here to help.
                        </p>
                        <button
                            onClick={() => window.location.href = '/contact'}
                            className="bg-widertalk-lime text-widertalk-navy px-6 py-3 rounded-lg font-semibold hover:bg-widertalk-lime/90 transition-all duration-300"
                        >
                            Contact Support
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
        ;
}
